import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { PoundSterling, Check, ArrowRight } from 'lucide-react';

const Pricing = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const highlights = [
    'Bespoke packages built around your exact requirements',
    'Flexible terms — scale up or down as your needs change',
    'No recruitment, training, or overhead costs',
    'Transparent pricing with no hidden fees'
  ];

  return (
    <section id="pricing" ref={ref} className="relative py-32 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 overflow-hidden">
      {/* Ambient background */}
      <div className="absolute inset-0 opacity-[0.12]">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(234,179,8,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(234,179,8,0.4) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-block px-4 py-2 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-semibold mb-6 border border-yellow-400/30">
            OUR PRICING
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Simple, Bespoke
            <span className="block bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent leading-tight pb-2">Pricing</span>
          </h2>
        </motion.div>

        {/* Price Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative bg-white/5 rounded-3xl border border-yellow-400/30 p-8 lg:p-14 shadow-2xl"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left — Price */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 text-yellow-400 mb-4">
                <PoundSterling className="w-6 h-6" />
                <span className="text-sm font-semibold uppercase tracking-widest">From</span>
              </div>
              <div className="flex items-end justify-center lg:justify-start gap-2 mb-2">
                <span className="text-7xl lg:text-8xl font-bold text-white leading-none">£600</span>
                <span className="text-xl text-slate-400 mb-3">/ month</span>
              </div>
              <p className="text-lg text-slate-300 mb-6">
                Ranging up to <span className="text-yellow-400 font-semibold">£1,000 per month</span> depending on the role.
              </p>
              <div className="p-5 bg-yellow-500/10 rounded-2xl border border-yellow-400/20">
                <p className="text-slate-200 leading-relaxed">
                  We keep it <span className="text-yellow-400 font-semibold">completely bespoke</span> — tailored to your
                  specific requirements and the period of support you need. No two businesses are the same, and neither are our packages.
                </p>
              </div>
            </div>

            {/* Right — Highlights */}
            <div>
              <ul className="space-y-4 mb-8">
                {highlights.map((item, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
                    transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="mt-0.5 w-6 h-6 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-slate-900" />
                    </div>
                    <span className="text-slate-200 text-lg">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="pricing-cta"
                className="group w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-slate-900 font-bold rounded-xl transition-all duration-300 shadow-lg shadow-yellow-500/40 hover:scale-[1.02]"
              >
                Get Your Bespoke Quote
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
